import { formatDateToYYYYMMDDKorean } from "@/common/util/date-util";
import { formatNumberWithCommas } from "@/common/util/format-number-with-commas";
import { OrderDetailDto } from "@/types/dto/order-detail-dto";
import { UserDto } from "@/types/dto/user-dto";
import {
  Document,
  Font,
  Image,
  Link,
  Page,
  PDFDownloadLink,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";

interface Props {
  orderDetail: OrderDetailDto;
  profile: UserDto;
  children?: React.ReactNode;
}

const MINIMUM_ROWS = 9;

// Noto Sans CJK 한글 폰트 추가 (Base64 또는 URL로 제공)
Font.register({
  family: "NotoSans",
  fonts: [
    {
      src: "https://fonts.gstatic.com/ea/notosanskr/v2/NotoSansKR-Regular.woff2",
    },
    {
      src: "https://fonts.gstatic.com/ea/notosanskr/v2/NotoSansKR-Bold.woff2",
    },
  ], // Noto Sans CJK 폰트
});

// 스타일 정의
const styles = StyleSheet.create({
  page: {
    fontFamily: "NotoSans", // 한글 폰트 사용
    padding: 20,
    fontSize: 10,
    position: "relative",
  },
  logo: {
    width: 130,
    marginBottom: 10, // 이미지와 타이틀 사이 간격
  },
  title: {
    fontSize: 18,
    fontWeight: "bold",
    textAlign: "center",
    backgroundColor: "#969696",
    color: "#ffffff",
    padding: "0 3px",
  },
  tableRow: {
    flexDirection: "row",
  },
  tableCol: {
    flex: 1,
    borderTop: 1,
    borderRight: 1,
    borderBottom: 0,
    borderLeft: 1,
    borderColor: "#808080",
    padding: "2px",
  },
  tableColLast: {
    flex: 1,
    borderTop: 1,
    borderRight: 1,
    borderBottom: 1,
    borderLeft: 1,
    borderColor: "#808080",
    padding: "2px",
  },
  tableColHeader: {
    width: 55,
    backgroundColor: "#c0c0c0",
    padding: "2px",
    textAlign: "center",
    color: "#000000",
    fontSize: 10,
    whiteSpace: "nowrap",
    borderTop: 1,
    borderRight: 1,
    borderBottom: 0,
    borderLeft: 1,
    borderColor: "#808080",
  },
  table2Col1: {
    fontWeight: "bold",
    width: 30,
    padding: "4px 2px",
    borderBottom: 1,
    borderRight: 1,
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center", // 수직 가운데 정렬
  },
  table2Col2: {
    fontWeight: "bold",
    width: 30,
    padding: "4px 2px",
    borderBottom: 1,
    borderRight: 1,
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center", // 수직 가운데 정렬
  },
  table2Col3: {
    fontWeight: "bold",
    width: 30,
    padding: "4px 2px",
    borderBottom: 1,
    borderRight: 1,
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center", // 수직 가운데 정렬
  },
  table2Col4: {
    fontWeight: "bold",
    width: 30,
    padding: "4px 2px",
    borderBottom: 1,
    borderRight: 1,
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center", // 수직 가운데 정렬
  },
  table2Col5: {
    fontWeight: "bold",
    width: 30,
    padding: "4px 2px",
    borderBottom: 1,
    borderRight: 1,
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center", // 수직 가운데 정렬
  },
  table2Col6: {
    fontWeight: "bold",
    width: 30,
    padding: "4px 2px",
    borderBottom: 1,
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center", // 수직 가운데 정렬
  },
  textCenter: {
    textAlign: "center",
    justifyContent: "center",
    alignItems: "center",
    display: "flex",
  },
  footer: {
    marginTop: 30,
    textAlign: "center",
  },
  emptyRow: {
    width: 30,
    padding: "4px 2px",
  },
});

// 컴포넌트: 테이블 헤더
const TableHeader = () => (
  <View style={styles.tableRow}>
    <View style={{ ...styles.table2Col1 }}>
      <Text style={styles.textCenter}>NO.</Text>
    </View>
    <View style={{ flex: 6, ...styles.table2Col2 }}>
      <Text style={styles.textCenter}>항목</Text>
    </View>
    <View style={{ flex: 2, ...styles.table2Col3 }}>
      <Text style={styles.textCenter}>Q'TY</Text>
    </View>
    <View style={{ flex: 2, ...styles.table2Col4 }}>
      <Text style={styles.textCenter}>정가</Text>
    </View>
    <View style={{ flex: 2, ...styles.table2Col5 }}>
      <Text style={styles.textCenter}>판매가</Text>
    </View>
    <View style={{ flex: 4, ...styles.table2Col6 }}>
      <Text style={styles.textCenter}>판매 금액</Text>
    </View>
  </View>
);

// 컴포넌트: 테이블 행
const TableRow = ({ item }: any) => (
  <View style={styles.tableRow}>
    <View style={{ ...styles.table2Col1 }}>
      <Text style={styles.textCenter}>{item.no}</Text>
    </View>
    <View style={{ flex: 6, ...styles.table2Col2 }}>
      <Text style={styles.textCenter}>{item.name}</Text>
    </View>
    <View style={{ flex: 2, ...styles.table2Col3 }}>
      <Text style={styles.textCenter}>{item.qty}</Text>
    </View>
    <View style={{ flex: 2, ...styles.table2Col4 }}>
      <Text style={styles.textCenter}>{item.price}</Text>
    </View>
    <View style={{ flex: 2, ...styles.table2Col5 }}>
      <Text style={styles.textCenter}>{item.supplyPrice}</Text>
    </View>
    <View style={{ flex: 4, ...styles.table2Col6 }}>
      <Text style={styles.textCenter}>{item.amount}</Text>
    </View>
  </View>
);

const TotalVatSumRow = ({ totalVatSum }: any) => (
  <View style={styles.tableRow}>
    <View style={{ flex: 4.65 }}></View>
    <View
      style={{
        flex: 2,
        ...styles.table2Col4,
        borderLeft: 1,
        padding: "0 5px",
      }}
    >
      <Text style={styles.textCenter}>Total 부가세</Text>
    </View>
    <View style={{ flex: 2, ...styles.table2Col5 }}>
      <Text style={styles.textCenter}>
        {formatNumberWithCommas(totalVatSum)}
      </Text>
    </View>
  </View>
);

const TotalSupplyPriceRow = ({ totalSupplyPrice }: any) => (
  <View style={styles.tableRow}>
    <View style={{ flex: 4.65 }}></View>
    <View
      style={{
        flex: 2,
        ...styles.table2Col4,
        borderLeft: 1,
        padding: "0 5px",
      }}
    >
      <Text style={styles.textCenter}>Total 공급가</Text>
    </View>
    <View style={{ flex: 2, ...styles.table2Col5 }}>
      <Text style={styles.textCenter}>
        {formatNumberWithCommas(totalSupplyPrice)}
      </Text>
    </View>
  </View>
);

const FinalPriceSumRow = ({ finalPriceSum }: any) => (
  <View style={styles.tableRow}>
    <View style={{ flex: 4.65 }}></View>
    <View
      style={{
        flex: 2,
        ...styles.table2Col4,
        backgroundColor: "#b4c6e7",
        fontWeight: "bold",
        borderLeft: 1,
        padding: "0 5px",
      }}
    >
      <Text style={styles.textCenter}>총 금액 (VAT 포함)</Text>
    </View>
    <View
      style={{
        flex: 2,
        ...styles.table2Col5,
        backgroundColor: "#b4c6e7",
        fontWeight: "bold",
      }}
    >
      <Text style={styles.textCenter}>
        {formatNumberWithCommas(finalPriceSum)}
      </Text>
    </View>
  </View>
);

// 컴포넌트: 상단 정보
const HeaderInfo = ({ label, value }: any) => (
  <View
    style={{
      display: "flex",
      flexDirection: "row",
      justifyContent: "flex-end",
    }}
  >
    <Text style={{ marginRight: "20px" }}>{label}</Text>
    <Text style={{ width: "110px" }}>{value}</Text>
  </View>
);

// PDF 문서 컴포넌트
const MyDocument: React.FC<Props> = ({ orderDetail, profile }) => {
  const processedItems =
    orderDetail.cartItems.length >= MINIMUM_ROWS
      ? orderDetail.cartItems
      : [
          ...orderDetail.cartItems,
          ...Array(MINIMUM_ROWS - orderDetail.cartItems.length)
            .fill(null)
            .map((_, index) => ({
              id: 0,
              index: orderDetail.cartItems.length + index,
              name: "-",
              erpCode: "-",
              price: 0,
              quantity: 0,
              originalPrice: 0,
              discountRate: 0,
              gradeDiscountRate: 0,
              discountPrice: 0,
              incentiveDiscountRate: 0,
              incentiveDiscountPrice: 0,
              totalPrice: 0,
              vat: 0,
              createdAt: "",
              updatedAt: "",
            })),
        ];

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Image style={styles.logo} src="/img/logo_black.png" />

        <Text style={styles.title}>발 주 서</Text>

        <HeaderInfo label="발주 번호" value={orderDetail.invoiceNumber} />
        <HeaderInfo
          label="발주 일자"
          value={formatDateToYYYYMMDDKorean(orderDetail.createdAt)}
        />

        <View style={{ marginTop: 10 }} />

        {/* 테이블 1 */}
        <View style={{ border: 1 }}>
          <View style={styles.tableRow}>
            <Text style={styles.tableColHeader}>수신</Text>
            <Text style={styles.tableCol}>{orderDetail.ordererName}</Text>
            <Text style={styles.tableColHeader}>발신</Text>
            <Text style={styles.tableCol}>아카라라이프</Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={styles.tableColHeader}>참조</Text>
            <Text style={styles.tableCol}>{profile.name}</Text>
            <Text style={styles.tableColHeader}>담당</Text>
            <Text style={styles.tableCol}>아카라라이프</Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={styles.tableColHeader}>T E L</Text>
            <Text style={styles.tableCol}>{orderDetail.ordererPhone}</Text>
            <Text style={styles.tableColHeader}>T E L</Text>
            <Text style={styles.tableCol}>02-6949-2662</Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={styles.tableColHeader}>이메일</Text>
            <Text style={styles.tableCol}>{orderDetail.ordererMail}</Text>
            <Text style={styles.tableColHeader}>이메일</Text>
            <Text style={styles.tableCol}>sales@aqara.kr</Text>
          </View>
        </View>

        <View
          style={{
            display: "flex",
            flexDirection: "row",
            margin: "4px 0",
          }}
        >
          <Text style={{ fontWeight: "bold" }}>공 사 명 : </Text>
          <Text>{orderDetail.constructionName}</Text>
        </View>

        <View
          style={{
            display: "flex",
            flexDirection: "row",
            margin: "4px 0 0 0",
          }}
        >
          <Text style={{ fontWeight: "bold" }}>공 종 명 : </Text>
          <Text>{orderDetail.constructionWorkName}</Text>
        </View>
        <View
          style={{ backgroundColor: "black", height: "0.5px", width: "100%" }}
        />
        <View
          style={{
            backgroundColor: "black",
            height: "0.5px",
            width: "100%",
            margin: "4px 0 0 0",
          }}
        />

        <View
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "flex-end",
            width: "100%",
            margin: "4px 0 0 0",
          }}
        >
          <Text>총 금액</Text>
          <View
            style={{
              flexGrow: 1,
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "flex-end",
              marginRight: 50,
            }}
          >
            <Text
              style={{
                fontWeight: "bold",
                fontSize: 14,
              }}
            >
              {formatNumberWithCommas(orderDetail.finalPriceSum)}원
            </Text>
          </View>
          <Text>(VAT포함, 원)</Text>
        </View>

        <View style={{ marginTop: 20, border: 1 }}>
          <TableHeader />
          {processedItems.map((item, index) => (
            <TableRow
              key={index}
              item={{
                no: item.index + 1,
                name: item.name || "-",
                qty: item.quantity || "-",
                price: formatNumberWithCommas(item.price) || "-",
                supplyPrice:
                  formatNumberWithCommas(
                    item.totalPrice / item.quantity || 0
                  ) || "-",
                amount: formatNumberWithCommas(item.totalPrice) || "-",
              }}
            />
          ))}
          <FinalPriceSumRow finalPriceSum={orderDetail.finalPriceSum} />
          <TotalSupplyPriceRow
            totalSupplyPrice={orderDetail.finalPriceSum - orderDetail.vatSum}
          />
          <TotalVatSumRow totalVatSum={orderDetail.vatSum} />
        </View>

        <Text style={{ marginTop: 20, fontSize: 9 }}>
          유지보수: 제품 보증 1년
        </Text>

        <View style={styles.footer}>
          <View
            style={{
              fontWeight: "bold",
              textAlign: "center",
              justifyContent: "center",
              backgroundColor: "#969696",
              color: "#ffffff",
              flexDirection: "row",
              fontSize: 11,
            }}
          >
            <Text>아카라라이프(주)</Text>
            <Link src={"https://aqaralife.kr/"}> https://aqaralife.kr/</Link>
          </View>
          <Text style={{ fontSize: 7, marginTop: 3 }}>
            서울시 서초구 강남대로 53길 8 마이워크스페이스타워 4층
          </Text>
          <Text style={{ fontSize: 7, marginTop: 3 }}>
            사업자 등록번호 : 585-88-01527 대표이사 김현철 업태/제조외
            종목/컴퓨터 및 통신기기
          </Text>
        </View>
        <Image
          style={{ position: "absolute", top: 125, right: 70, width: 45 }}
          src="/img/seal.png"
        />
      </Page>
    </Document>
  );
};

export const OrderPDFDownloadButton: React.FC<Props> = ({
  orderDetail,
  profile,
  children,
}) => (
  <div
    style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
  >
    {/* PDF 미리보기
    <PDFViewer style={{ width: "80%", height: "600px", marginBottom: "20px" }}>
      <MyDocument />
    </PDFViewer> */}

    {/* PDF 다운로드 링크 */}
    <PDFDownloadLink
      document={<MyDocument orderDetail={orderDetail} profile={profile} />}
      fileName="발주서.pdf"
    >
      {children}
    </PDFDownloadLink>
  </div>
);
